import { z } from 'zod';

/** Platform console contracts (ADR-0030). */

export const subscriptionStatusSchema = z.enum([
  'trialing',
  'active',
  'past_due',
  'read_only',
  'suspended',
  'cancelled',
]);

export const platformTenantSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  legal_name: z.string().nullable(),
  billing_email: z.string().nullable(),
  status: z.enum(['active', 'suspended']),
  plan_code: z.string(),
  subscription_status: subscriptionStatusSchema,
  trial_ends_at: z.string().nullable(),
  current_period_end: z.string().nullable(),
  branches: z.number().int(),
  staff: z.number().int(),
  /** Active enrolled devices. */
  devices: z.number().int(),
  last_activity_at: z.string().nullable(),
  created_at: z.string(),
});
export type PlatformTenant = z.infer<typeof platformTenantSchema>;

export const platformTenantListSchema = z.object({ items: z.array(platformTenantSchema) });

export const createTenantRequestSchema = z.object({
  name: z.string().trim().min(1).max(120),
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/, 'Lowercase letters, digits and dashes (3–40)'),
  legal_name: z.string().trim().max(200).nullable().default(null),
  billing_email: z.email().max(254),
  plan_code: z.string().min(1).max(40),
  trial_days: z.number().int().min(0).max(90).default(30),
  owner: z.object({
    email: z.email().max(254),
    display_name: z.string().trim().min(1).max(120),
    locale: z.enum(['en', 'id']).default('id'),
  }),
  branch: z.object({
    code: z.string().regex(/^[A-Z0-9-]{2,16}$/),
    name: z.string().trim().min(1).max(120),
    time_zone: z.string().default('Asia/Jakarta'),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .default('IDR'),
    locale: z.enum(['en', 'id']).default('id'),
    tax_rate_bps: z.number().int().min(0).max(5000).default(1000),
    service_charge_bps: z.number().int().min(0).max(5000).default(0),
  }),
});
export type CreateTenantRequest = z.input<typeof createTenantRequestSchema>;

export const createTenantResponseSchema = z.object({
  tenant: platformTenantSchema,
  owner_user_id: z.string(),
  owner_is_new_user: z.boolean(),
  /** Present for a new owner: the invitation link was e-mailed; shown once for manual delivery. */
  invite_url: z.string().nullable(),
});

// ---------------------------------------------------------------------------------------------
// Platform console (roadmap S2, ADR-0032)
// ---------------------------------------------------------------------------------------------

export const platformRoleSchema = z.enum(['owner', 'operator', 'support']);
export type PlatformRole = z.infer<typeof platformRoleSchema>;

/** Plan modules (mirror of `MODULES` in the Worker's entitlements service; a test keeps them equal). */
export const PLAN_MODULES = [
  'pos',
  'kds',
  'service',
  'guest_qr',
  'guest_ordering',
  'inventory',
  'procurement',
  'ap',
  'approvals',
  'budgets',
  'custom_domain',
  'warehouse',
] as const;
export const planModuleSchema = z.enum(PLAN_MODULES);
export type PlanModule = z.infer<typeof planModuleSchema>;

export const platformMeSchema = z.object({ role: platformRoleSchema });

/** `GET /v1/platform/tenants` filters (all optional; `q` matches name, slug or billing e-mail). */
export const platformTenantListQuerySchema = z.object({
  q: z.string().trim().max(100).optional(),
  status: subscriptionStatusSchema.optional(),
  plan: z.string().trim().min(1).max(40).optional(),
});
export type PlatformTenantListQuery = z.infer<typeof platformTenantListQuerySchema>;

export const platformAuditEntrySchema = z.object({
  id: z.string(),
  actor_user_id: z.string(),
  actor_name: z.string().nullable(),
  actor_email: z.string().nullable(),
  action: z.string(),
  tenant_id: z.string().nullable(),
  tenant_name: z.string().nullable(),
  changes: z.record(z.string(), z.unknown()).nullable(),
  request_id: z.string(),
  created_at: z.string(),
});
export type PlatformAuditEntry = z.infer<typeof platformAuditEntrySchema>;

export const platformAuditQuerySchema = z.object({
  tenant_id: z.string().max(64).optional(),
  /** `next_cursor` of the previous page. */
  cursor: z.string().max(64).optional(),
});

export const platformAuditPageSchema = z.object({
  items: z.array(platformAuditEntrySchema),
  next_cursor: z.string().nullable(),
});

export const supportGrantStatusSchema = z.enum([
  'requested',
  'approved',
  'rejected',
  'revoked',
  /** Approved, but the time box has passed (derived; stored status stays `approved`). */
  'expired',
]);
export type SupportGrantStatus = z.infer<typeof supportGrantStatusSchema>;

export const supportGrantSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  requested_by: z.string(),
  requested_by_name: z.string().nullable(),
  requested_by_email: z.string().nullable(),
  reason: z.string(),
  status: supportGrantStatusSchema,
  /** Length of the access window (1–24 h); it starts when an owner approves. */
  hours: z.number().int(),
  decided_by: z.string().nullable(),
  decided_by_name: z.string().nullable(),
  decided_at: z.string().nullable(),
  /** End of the access window; null until approved. */
  expires_at: z.string().nullable(),
  created_at: z.string(),
});
export type SupportGrant = z.infer<typeof supportGrantSchema>;

export const supportGrantListSchema = z.object({ items: z.array(supportGrantSchema) });

export const requestSupportAccessSchema = z.object({
  reason: z.string().trim().min(10).max(500),
  hours: z.number().int().min(1).max(24),
});
export type RequestSupportAccess = z.infer<typeof requestSupportAccessSchema>;

export const platformTenantDetailSchema = z.object({
  tenant: platformTenantSchema.extend({
    tax_id: z.string().nullable(),
    cancelled_at: z.string().nullable(),
    updated_at: z.string(),
  }),
  branches: z.array(
    z.object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      status: z.enum(['active', 'inactive']),
    }),
  ),
  owners: z.array(
    z.object({
      user_id: z.string(),
      display_name: z.string(),
      email: z.string(),
      membership_status: z.enum(['invited', 'active', 'deactivated']),
    }),
  ),
  support_grants: z.array(supportGrantSchema),
  audit: z.array(platformAuditEntrySchema),
});
export type PlatformTenantDetail = z.infer<typeof platformTenantDetailSchema>;

export const suspendTenantRequestSchema = z.object({
  reason: z.string().trim().min(3).max(500),
});
export const reactivateTenantRequestSchema = z.object({
  reason: z.string().trim().max(500).optional(),
});
export const extendTrialRequestSchema = z.object({
  days: z.number().int().min(1).max(60),
});
export const changePlanRequestSchema = z.object({
  plan_code: z.string().trim().min(1).max(40),
});
export const tenantActionResponseSchema = z.object({ tenant: platformTenantSchema });

export const planLimitsSchema = z.object({
  max_branches: z.number().int().min(1).max(100_000).nullable(),
  max_staff: z.number().int().min(1).max(100_000).nullable(),
  max_devices: z.number().int().min(1).max(100_000).nullable(),
});

export const planSchema = z.object({
  code: z.string(),
  name: z.string(),
  price_minor: z.number().int(),
  currency: z.string(),
  interval: z.enum(['month', 'year']),
  limits: planLimitsSchema,
  modules: z.array(z.string()),
  public: z.boolean(),
  active: z.boolean(),
  sort_order: z.number().int(),
  updated_at: z.string(),
});
export type Plan = z.infer<typeof planSchema>;
export const planListSchema = z.object({ items: z.array(planSchema) });

/** PATCH: no `.default()` (zod 4 `.partial()` would keep it and reset omitted fields). */
export const updatePlanRequestSchema = z.object({
  /** Optimistic concurrency: the plan's `updated_at` as read. */
  expected_updated_at: z.string().min(1).max(40),
  name: z.string().trim().min(1).max(80).optional(),
  price_minor: z.number().int().min(0).max(1_000_000_000_000).optional(),
  limits: planLimitsSchema.optional(),
  modules: z
    .array(planModuleSchema)
    .max(PLAN_MODULES.length)
    .refine((m) => new Set(m).size === m.length, 'Duplicate module')
    .optional(),
  public: z.boolean().optional(),
  active: z.boolean().optional(),
});
export type UpdatePlanRequest = z.infer<typeof updatePlanRequestSchema>;

const optionalText = (max: number) => z.string().trim().max(max).nullable();

/** `platform_settings.billing` (seeded by migration 0018). Unknown keys are kept. */
export const billingSettingsSchema = z
  .object({
    tax_rate_bps: z.number().int().min(0).max(5000),
    payment_terms_days: z.number().int().min(0).max(90),
    grace_days: z.number().int().min(0).max(60),
    read_only_after_days: z.number().int().min(0).max(90),
    trial_days: z.number().int().min(0).max(90),
    issuer: z
      .object({
        legal_name: z.string().trim().min(1).max(200),
        address: optionalText(500),
        tax_id: optionalText(40),
        bank_account: optionalText(200),
      })
      .loose(),
  })
  .loose();
export type BillingSettings = z.infer<typeof billingSettingsSchema>;

/** `platform_settings.legal` (seeded by migration 0018). Unknown keys are kept. */
export const legalSettingsSchema = z
  .object({
    terms_version: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9._-]{1,40}$/),
    terms_url: z.url().max(500).nullable(),
    privacy_url: z.url().max(500).nullable(),
  })
  .loose();
export type LegalSettings = z.infer<typeof legalSettingsSchema>;

export const PLATFORM_SETTING_KEYS = ['billing', 'legal'] as const;
export type PlatformSettingKey = (typeof PLATFORM_SETTING_KEYS)[number];

export const billingSettingResponseSchema = z.object({
  key: z.literal('billing'),
  value: billingSettingsSchema,
  updated_by: z.string().nullable(),
  updated_at: z.string(),
});
export const legalSettingResponseSchema = z.object({
  key: z.literal('legal'),
  value: legalSettingsSchema,
  updated_by: z.string().nullable(),
  updated_at: z.string(),
});

/** PATCH settings: the new value (top-level keys replace the stored ones) + concurrency token. */
export const updateBillingSettingsRequestSchema = z.object({
  expected_updated_at: z.string().min(1).max(40),
  value: billingSettingsSchema.partial(),
});
export const updateLegalSettingsRequestSchema = z.object({
  expected_updated_at: z.string().min(1).max(40),
  value: legalSettingsSchema.partial(),
});

export const platformAdminSchema = z.object({
  user_id: z.string(),
  email: z.string(),
  display_name: z.string(),
  role: platformRoleSchema,
  created_by: z.string().nullable(),
  created_at: z.string(),
});
export type PlatformAdmin = z.infer<typeof platformAdminSchema>;
export const platformAdminListSchema = z.object({ items: z.array(platformAdminSchema) });

export const addPlatformAdminRequestSchema = z.object({
  email: z.email().max(254),
  role: platformRoleSchema,
});

/** Read-only snapshot shown to a platform admin under an approved support grant (D8). */
export const supportOverviewSchema = z.object({
  tenant: z.object({ id: z.string(), name: z.string(), slug: z.string() }),
  grant: z.object({ id: z.string(), expires_at: z.string() }),
  generated_at: z.string(),
  branches: z.array(
    z.object({
      id: z.string(),
      code: z.string(),
      name: z.string(),
      status: z.enum(['active', 'inactive']),
      currency: z.string(),
      business_date: z.string(),
      orders_today: z.number().int(),
      /** Bills of today's business date that are not void, in minor units. */
      sales_today_minor: z.number().int(),
      open_orders: z.number().int(),
      open_tables: z.number().int(),
    }),
  ),
  audit: z.array(
    z.object({
      id: z.string(),
      branch_id: z.string().nullable(),
      actor_user_id: z.string().nullable(),
      action: z.string(),
      entity_type: z.string(),
      entity_id: z.string(),
      reason: z.string().nullable(),
      created_at: z.string(),
    }),
  ),
});
export type SupportOverview = z.infer<typeof supportOverviewSchema>;
